import React from 'react';
import PropTypes from 'prop-types';
import {
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  Typography,
  styled,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Styled Accordion component
const StyledAccordion = styled(MuiAccordion)(({ theme }) => ({
  borderRadius: '16px !important',
  overflow: 'hidden',
  marginBottom: '16px',
  boxShadow: 'none',
  border: `1px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.12)'
      : 'rgba(255, 255, 255, 0.12)'
  }`,
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '0 0 16px 0',
  },
}));

// Styled AccordionSummary component
const StyledAccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.03)'
      : 'rgba(255, 255, 255, 0.05)',
  minHeight: '56px',
  '&.Mui-expanded': {
    minHeight: '56px',
  },
  '& .MuiAccordionSummary-content': {
    margin: '12px 0',
    '&.Mui-expanded': {
      margin: '12px 0',
    },
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(180deg)',
  },
}));

// Styled AccordionDetails component
const StyledAccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: '16px',
  borderTop: `1px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.12)'
      : 'rgba(255, 255, 255, 0.12)'
  }`,
}));

// Accordion component
const Accordion = ({
  title,
  children,
  defaultExpanded = false,
  disabled = false,
  ...props
}) => {
  return (
    <StyledAccordion
      defaultExpanded={defaultExpanded}
      disabled={disabled}
      {...props}
    >
      <StyledAccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel-content"
        id="panel-header"
      >
        <Typography variant="subtitle1" fontWeight={500}>
          {title}
        </Typography>
      </StyledAccordionSummary>
      <StyledAccordionDetails>{children}</StyledAccordionDetails>
    </StyledAccordion>
  );
};

// AccordionGroup component to manage multiple accordions
const AccordionGroup = ({ children, allowMultiple = false, ...props }) => {
  const [expanded, setExpanded] = React.useState(allowMultiple ? [] : '');

  const handleChange = (panel) => (event, isExpanded) => {
    if (allowMultiple) {
      setExpanded((prev) => {
        if (isExpanded) {
          return [...prev, panel];
        } else {
          return prev.filter((item) => item !== panel);
        }
      });
    } else {
      setExpanded(isExpanded ? panel : '');
    }
  };

  return (
    <div {...props}>
      {React.Children.map(children, (child, index) => {
        const panel = `panel${index}`;
        return React.cloneElement(child, {
          expanded: allowMultiple
            ? expanded.includes(panel)
            : expanded === panel,
          onChange: handleChange(panel),
        });
      })}
    </div>
  );
};

Accordion.propTypes = {
  /**
   * The title of the accordion.
   */
  title: PropTypes.node.isRequired,
  /**
   * The content of the accordion.
   */
  children: PropTypes.node.isRequired,
  /**
   * If true, the accordion will be expanded by default.
   */
  defaultExpanded: PropTypes.bool,
  /**
   * If true, the accordion will be disabled.
   */
  disabled: PropTypes.bool,
};

AccordionGroup.propTypes = {
  /**
   * The accordion components.
   */
  children: PropTypes.node.isRequired,
  /**
   * If true, multiple accordions can be expanded at the same time.
   */
  allowMultiple: PropTypes.bool,
};

export { Accordion, AccordionGroup };
